import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Alert,
    Breadcrumbs,
    Button,
    ButtonGroup,
    Divider,
    Fade,
    Snackbar,
    TextField,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
    selectEmail,
    selectMaxAdditionalUsers,
    selectName,
    selectUserLoading,
    selectUserPermissions,
} from '../../store/reducers/userSlice';
import LoadPage from '../misc/LoadPage';
import { SubscriptionRequired } from '../misc/SubscriptionRequired';
import {
    EmailPreference,
    getAdditionalEmails,
    getCommitteeEmailPreferences,
    getInsiderEmailPreferences,
    getIssueEmailPreferences,
    getSectorEmailPreferences,
    selectAdditionalEmails,
    selectDraftAdditionalEmails,
    selectDraftCommitteePreferences,
    selectDraftInsiderPreferences,
    selectDraftIssuePreferences,
    selectDraftSectorPreferences,
    selectSavePreferencesError,
    selectSavePreferencesLoading,
    selectSavePreferencesSuccess,
    setDraftAdditionalEmails,
    setDraftCommitteeEmailPreferences,
    setDraftInsiderEmailPreferences,
    setDraftIssueEmailPreferences,
    setDraftSectorEmailPreferences,
    setEmailPreferences,
} from '../../store/reducers/userPreferencesSlice';
import { useAppDispatch } from '../../store/store';
import {
    fetchCommitteesSelections,
    fetchSectorsSelections,
    selectChosenCommittees,
    selectChosenSectors,
    Selection,
} from '../../store/reducers/customSelectionSlice';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

import {
    fetchCustomIssueList,
    fetchIssueList,
    selectCustomIssueList,
    selectIssueList,
} from '../../features/issue-dashboards/store/issueListSlice';
import { entityListExtra } from '../../interfaces/generic-entity.interface';
import { emailPreferencesListComponent } from './EmailPreferencesListComponent';
import validator from 'validator';
import LoadSpinner from 'components/misc/LoadSpinner';
import { issueEditorItem } from 'features/issue-dashboards/interface/issue-entity.interface';

const EmailPreferencesPage = () => {
    const dispatch = useAppDispatch();
    const userLoading = useSelector(selectUserLoading);
    const primaryEmail = useSelector(selectEmail);
    const primaryEmailName = useSelector(selectName);
    const permissions = useSelector(selectUserPermissions);
    const additionalEmails = useSelector(selectAdditionalEmails);
    const draftAdditionalEmails = useSelector(selectDraftAdditionalEmails);
    const draftIssuePreferences = useSelector(selectDraftIssuePreferences);
    const draftCommitteePreferences = useSelector(selectDraftCommitteePreferences);
    const draftSectorPreferences = useSelector(selectDraftSectorPreferences);
    const draftInsiderPreferences = useSelector(selectDraftInsiderPreferences);
    const maxAdditionalUsers = useSelector(selectMaxAdditionalUsers);
    const sectorSelections = useSelector(selectChosenSectors);
    const committeeSelections = useSelector(selectChosenCommittees);
    const issueList = useSelector(selectIssueList);
    const persionalIssues = useSelector(selectCustomIssueList);
    const allIssues = useSelector(selectIssueList);
    const savePreferencesLoading = useSelector(selectSavePreferencesLoading);
    const savePreferencesSuccess = useSelector(selectSavePreferencesSuccess);
    const savePreferencesError = useSelector(selectSavePreferencesError);

    const lobbyIQIssues = allIssues
        ?.filter(
            (issue) => !persionalIssues.find((personalIssue) => personalIssue.code === issue.code)
        )
        .sort((a, b) => a.title.localeCompare(b.title));

    const selectedLobbyIQIssues = lobbyIQIssues?.filter(
        (issue) =>
            !!draftIssuePreferences?.find((preference) => preference.code === issue.code.toString())
    );

    const combinedIssueList = [...persionalIssues, ...(selectedLobbyIQIssues ?? [])];

    const [type, setType] = useState<'issue' | 'committee' | 'sector' | 'insider'>('issue');
    const [saveSuccessPopup, setSaveSuccessPopup] = useState(false);

    useEffect(() => {
        dispatch(getAdditionalEmails());
        dispatch(fetchSectorsSelections());
        dispatch(fetchCommitteesSelections());
        dispatch(fetchIssueList());
        dispatch(fetchCustomIssueList());
        dispatch(getIssueEmailPreferences());
        dispatch(getCommitteeEmailPreferences());
        dispatch(getSectorEmailPreferences());
        dispatch(getInsiderEmailPreferences());
    }, [dispatch]);

    // TODO display all sectors for enterprise users
    // useEffect(() => {
    //     if(permissions && permissions.productType === ProductType.ENTERPRISE) {
    //         dispatch(fetchSectorList());
    //     }
    // }, [dispatch, permissions]);

    useEffect(() => {
        setDraftAdditionalEmails(additionalEmails);
    }, [additionalEmails]);

    useEffect(() => {
        if (savePreferencesSuccess) {
            setSaveSuccessPopup(true);
        } else {
            setSaveSuccessPopup(false);
        }
    }, [savePreferencesSuccess]);

    // emails must be unique and valid
    const canSave = () => {
        return draftAdditionalEmails?.every(
            (user, index) =>
                (validator.isEmail(user.email) &&
                    user.name !== '' &&
                    draftAdditionalEmails.filter(
                        (user) => user.email === draftAdditionalEmails[index]?.email
                    ).length <= 1) ||
                user.email === ''
        );
    };

    const addEmailToAllPreferences = (
        email: string,
        name: string,
        existingPreferences: EmailPreference[],
        // just need code and title for this array
        selectionList: Selection[] | (entityListExtra | issueEditorItem)[]
    ) => {
        // preserve existing preferences while adding the new email to each
        return selectionList.map((selection) => {
            const existingPreference = existingPreferences.find(
                (preference) => preference.code === selection.code.toString()
            );
            if (existingPreference) {
                return {
                    ...existingPreference,
                    emails: [...existingPreference.emails, { email, name }],
                };
            } else {
                return {
                    code: selection.code.toString(),
                    emails: [{ email, name }],
                    title: selection.title,
                };
            }
        });
    };

    const handleAdditonalEmailChange = (index: number, email: string, name: string) => {
        // new email
        if (draftAdditionalEmails!.length <= index) {
            let newDraftAdditionalEmails = [...draftAdditionalEmails!];

            while (newDraftAdditionalEmails.length <= index) {
                if (newDraftAdditionalEmails.length === index) {
                    newDraftAdditionalEmails.push({ email, name });
                } else {
                    newDraftAdditionalEmails.push({ email: '', name: '' });
                }
            }

            dispatch(setDraftAdditionalEmails(newDraftAdditionalEmails));
        } else {
            // edit existing email (may be invalid)
            const oldEmail = draftAdditionalEmails![index]?.email || null;
            if (oldEmail && oldEmail !== email) {
                // clear old email from draft preferences if it has been edited
                const filteredIssuePreferences = filterOldEmail(oldEmail, draftIssuePreferences!);
                const filteredCommitteePreferences = filterOldEmail(
                    oldEmail,
                    draftCommitteePreferences!
                );
                const filteredSectorPreferences = filterOldEmail(oldEmail, draftSectorPreferences!);
                const filteredInsiderPreferences = draftInsiderPreferences!.filter(
                    (preference) => preference.email !== oldEmail
                );

                // default all emails to true for the new email (if valid)
                if (validator.isEmail(email)) {
                    dispatch(
                        setDraftIssueEmailPreferences(
                            addEmailToAllPreferences(
                                email,
                                name,
                                filteredIssuePreferences,
                                combinedIssueList!
                            )
                        )
                    );
                    dispatch(
                        setDraftCommitteeEmailPreferences(
                            addEmailToAllPreferences(
                                email,
                                name,
                                filteredCommitteePreferences,
                                committeeSelections
                            )
                        )
                    );
                    dispatch(
                        setDraftSectorEmailPreferences(
                            addEmailToAllPreferences(
                                email,
                                name,
                                filteredSectorPreferences,
                                sectorSelections
                            )
                        )
                    );
                    dispatch(
                        setDraftInsiderEmailPreferences([
                            ...filteredInsiderPreferences,
                            {
                                email,
                                name:
                                    draftAdditionalEmails?.find((e) => e.email === email)?.name ??
                                    '',
                            },
                        ])
                    );
                } else {
                    // if email is invalid, just clear the draft preferences
                    dispatch(setDraftIssueEmailPreferences(filteredIssuePreferences));
                    dispatch(setDraftCommitteeEmailPreferences(filteredCommitteePreferences));
                    dispatch(setDraftSectorEmailPreferences(filteredSectorPreferences));
                    dispatch(setDraftInsiderEmailPreferences(filteredInsiderPreferences));
                }
            } else {
                // if email is invalid, just edit the name
                dispatch(
                    setDraftIssueEmailPreferences(
                        editNameForPreferences(email, name, draftIssuePreferences!)
                    )
                );
                dispatch(
                    setDraftCommitteeEmailPreferences(
                        editNameForPreferences(email, name, draftCommitteePreferences!)
                    )
                );
                dispatch(
                    setDraftSectorEmailPreferences(
                        editNameForPreferences(email, name, draftSectorPreferences!)
                    )
                );
                dispatch(
                    setDraftInsiderEmailPreferences(
                        draftInsiderPreferences!.map((existingEmail) => {
                            if (existingEmail.email === email) {
                                return { ...existingEmail, name };
                            }
                            return existingEmail;
                        })
                    )
                );
            }

            // update draft additional emails
            dispatch(
                setDraftAdditionalEmails(
                    draftAdditionalEmails!.map((additionalEmail, i) => {
                        if (i === index) {
                            return { email, name };
                        } else {
                            return additionalEmail;
                        }
                    })
                )
            );
        }
    };

    const filterOldEmail = (oldEmail: string, preferenceList: EmailPreference[]) => {
        return preferenceList!.map((preference) => {
            if (preference.emails.some((email) => email.email === oldEmail)) {
                return {
                    ...preference,
                    emails: preference.emails.filter((email) => email.email !== oldEmail),
                };
            }
            return preference;
        });
    };

    const editNameForPreferences = (
        email: string,
        name: string,
        preferenceList: EmailPreference[]
    ) => {
        return preferenceList!.map((preference) => {
            return {
                ...preference,
                emails: preference.emails.map((existingEmail) => {
                    if (existingEmail.email === email) {
                        return { ...existingEmail, name };
                    }
                    return existingEmail;
                }),
            };
        });
    };

    const handleAddIssue = (issue: entityListExtra) => {
        dispatch(
            setDraftIssueEmailPreferences([
                ...(draftIssuePreferences ?? []),
                {
                    code: issue.code.toString(),
                    emails: [
                        { email: primaryEmail!, name: primaryEmailName! },
                        ...(draftAdditionalEmails ?? []),
                    ],
                    title: issue.title,
                },
            ])
        );
    };
    const handleRemoveIssue = (issue: entityListExtra) => {
        dispatch(
            setDraftIssueEmailPreferences([
                ...(draftIssuePreferences ?? []).filter((p) => p.code !== issue.code.toString()),
            ])
        );
    };

    // TODO fix name: undefined undefined
    const toggleEmailPreference = (
        preferenceList: EmailPreference[],
        code: string,
        title: string,
        email: string
    ): EmailPreference[] => {
        const existingPreference = preferenceList!.find(
            (preference) => preference.code.toString() === code
        );

        if (existingPreference && existingPreference.emails.some((e) => e.email === email)) {
            // if email exists, remove it
            return [
                ...preferenceList!.filter((p) => p.code.toString() !== code),
                {
                    ...existingPreference,
                    emails: existingPreference.emails.filter((e) => e.email !== email),
                },
            ];
        } else if (existingPreference) {
            // if email doesn't exist, add it
            return [
                ...preferenceList!.filter((p) => p.code.toString() !== code),
                {
                    ...existingPreference,
                    emails: [
                        ...existingPreference.emails,
                        {
                            email,
                            name:
                                existingPreference.emails.find((e) => e.email === email)?.name ??
                                '',
                        },
                    ],
                },
            ];
        }

        // add the whole preference object if it doesn't exist
        return [
            ...preferenceList,
            {
                code,
                emails: [
                    {
                        email,
                        name: draftAdditionalEmails?.find((e) => e.email === email)?.name ?? '',
                    },
                ],
                title,
            },
        ];
    };

    const handleEmailPreferenceChange = (email: string, code?: string, title?: string) => {
        if (type === 'issue') {
            dispatch(
                setDraftIssueEmailPreferences(
                    toggleEmailPreference(draftIssuePreferences!, code!, title!, email)
                )
            );
        } else if (type === 'committee') {
            dispatch(
                setDraftCommitteeEmailPreferences(
                    toggleEmailPreference(draftCommitteePreferences!, code!, title!, email)
                )
            );
        } else if (type === 'sector') {
            dispatch(
                setDraftSectorEmailPreferences(
                    toggleEmailPreference(draftSectorPreferences!, code!, title!, email)
                )
            );
        } else if (type === 'insider') {
            dispatch(
                setDraftInsiderEmailPreferences(
                    draftInsiderPreferences!.some((selectedEmail) => selectedEmail.email === email)
                        ? draftInsiderPreferences!.filter(
                              (selectedEmail) => selectedEmail.email !== email
                          )
                        : [
                              ...(draftInsiderPreferences ?? []),
                              {
                                  email,
                                  name:
                                      draftAdditionalEmails?.find((e) => e.email === email)?.name ??
                                      '',
                              },
                          ]
                )
            );
        }
    };

    return userLoading ? (
        <LoadPage />
    ) : !permissions ? (
        <SubscriptionRequired />
    ) : (
        <Fade in={true}>
            <div className="lg:py-32 py-20 mx-auto lg:w-1/2 w-full flex flex-col lg:px-6 px-3">
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    open={saveSuccessPopup}
                    autoHideDuration={3000}
                    onClose={() => setSaveSuccessPopup(false)}
                >
                    <Alert onClose={() => setSaveSuccessPopup(false)} severity="success">
                        Email preferences saved
                    </Alert>
                </Snackbar>

                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    open={savePreferencesError}
                    autoHideDuration={3000}
                >
                    <Alert severity="error">Error saving email preferences</Alert>
                </Snackbar>

                <Breadcrumbs separator=">">
                    <Link to="/" className="hover:underline">
                        Lobby<b>IQ</b>
                    </Link>
                    <div>Email Preferences</div>
                </Breadcrumbs>

                <div className="mt-16">
                    <div className="text-4xl flex flex-row gap-2">Email Preferences</div>
                </div>

                <div className="flex flex-col">
                    <div className="flex flex-col mt-12">
                        <div className="text-2xl flex flex-row">Primary User Email</div>
                    </div>
                    <div className="flex flex-row gap-2">
                        <div className="lg:w-80 min-w-56 lg:pr-4 md:pr-4 my-4">
                            <TextField
                                fullWidth
                                id={`email-primary`}
                                variant="outlined"
                                required
                                value={primaryEmail}
                                InputProps={{
                                    readOnly: true,
                                }}
                                inputProps={{
                                    style: { textAlign: 'center' },
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col mt-6">
                        <div className="text-2xl flex flex-row gap-2 mb-3">Additional Users</div>
                    </div>
                    <div className="flex flex-row gap-2">
                        <div className="lg:w-80 min-w-56 my-3 pl-3 font-bold">Name</div>
                        <div className="lg:w-80 min-w-56 my-3 pl-3 font-bold">Business Email</div>
                    </div>
                    {[...Array(maxAdditionalUsers)].map((_, index) => (
                        <div className="flex flex-row gap-2">
                            <div
                                key={`email-${index}-container`}
                                className="lg:w-80 min-w-56 lg:pr-4 md:pr-4 my-3"
                            >
                                <TextField
                                    fullWidth
                                    id={`name-${index}`}
                                    placeholder="Full Name"
                                    variant="outlined"
                                    required
                                    value={
                                        draftAdditionalEmails &&
                                        draftAdditionalEmails.length > index
                                            ? draftAdditionalEmails[index].name
                                            : ''
                                    }
                                    error={
                                        draftAdditionalEmails &&
                                        draftAdditionalEmails.length > index &&
                                        validator.isEmail(draftAdditionalEmails[index].email) &&
                                        draftAdditionalEmails[index].name === ''
                                    }
                                    onChange={(e) => {
                                        handleAdditonalEmailChange(
                                            index,
                                            draftAdditionalEmails![index]?.email ?? '',
                                            e.target.value
                                        );
                                    }}
                                />
                                <Divider sx={{ marginTop: 3, marginLeft: 1, width: '109%' }} />
                            </div>
                            <div
                                key={`name-${index}-container`}
                                className="lg:w-80 min-w-56 lg:pr-4 md:pr-4 my-3"
                            >
                                <TextField
                                    fullWidth
                                    id={`email-${index}`}
                                    placeholder="Business Email"
                                    variant="outlined"
                                    required
                                    error={
                                        draftAdditionalEmails &&
                                        draftAdditionalEmails.length > index &&
                                        validator.isEmail(draftAdditionalEmails[index].email) &&
                                        // emails must be unique
                                        draftAdditionalEmails.filter(
                                            (user) =>
                                                user.email === draftAdditionalEmails[index].email
                                        ).length > 1
                                    }
                                    value={
                                        draftAdditionalEmails &&
                                        draftAdditionalEmails.length > index
                                            ? draftAdditionalEmails[index].email
                                            : ''
                                    }
                                    onChange={(e) => {
                                        handleAdditonalEmailChange(
                                            index,
                                            e.target.value,
                                            draftAdditionalEmails![index]?.name ?? ''
                                        );
                                    }}
                                />
                                <Divider sx={{ marginTop: 3, marginLeft: 1, width: '92%' }} />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col mt-6">
                    <div className="text-2xl flex flex-row mb-3">
                        Set email preferences for Primary and Additional Users
                    </div>
                    <div className="text-xl flex flex-row">
                        Click on each box below and press Save after making selections
                    </div>
                    <div className="mt-5">
                        {!canSave() && (
                            <div className="text-red-600 mb-4">
                                Please ensure each additional user has a name and valid business
                                email.
                            </div>
                        )}
                        <div className="w-1/4 mb-5">
                            {savePreferencesLoading ? (
                                <LoadSpinner />
                            ) : (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => dispatch(setEmailPreferences())}
                                    disabled={!canSave()}
                                    endIcon={<SaveRoundedIcon />}
                                >
                                    Save
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-row justify-between items-center">
                        <ButtonGroup
                            disableElevation
                            size="medium"
                            sx={{
                                '& .MuiButton-root': {
                                    fontFamily: 'Roboto',
                                    textTransform: 'none',
                                },
                            }}
                            color={
                                type === 'issue'
                                    ? 'info'
                                    : type === 'committee'
                                    ? 'secondary'
                                    : type === 'sector'
                                    ? 'success'
                                    : 'error'
                            }
                        >
                            <Button
                                onClick={() => setType('issue')}
                                variant={type === 'issue' ? 'contained' : 'outlined'}
                            >
                                Daily Issue Tracker Email
                            </Button>

                            <Button
                                onClick={() => setType('committee')}
                                variant={type === 'committee' ? 'contained' : 'outlined'}
                            >
                                Committee Meeting Transcripts
                            </Button>

                            <Button
                                onClick={() => setType('sector')}
                                variant={type === 'sector' ? 'contained' : 'outlined'}
                            >
                                Weekly Lobby Report Email
                            </Button>
                            <Button
                                onClick={() => setType('insider')}
                                variant={type === 'insider' ? 'contained' : 'outlined'}
                            >
                                GR Insider Email
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>

                <div className="text-xl flex flex-row mt-5 items-center">
                    {type === 'sector'
                        ? sectorSelections.length > 0
                            ? 'Select Sectors for which users will receive weekly lobby report emails'
                            : 'No Sectors selected'
                        : type === 'committee'
                        ? committeeSelections.length > 0
                            ? 'Select Committees for which users will receive meeting transcripts and summaries'
                            : 'No Committees selected'
                        : type === 'issue'
                        ? 'Select Issues for which users will receive daily Issue Tracker emails'
                        : 'Select which users will receive daily GR Insider emails'}
                </div>

                {emailPreferencesListComponent({
                    type,
                    displayList:
                        (type === 'issue'
                            ? issueList
                            : type === 'committee'
                            ? committeeSelections
                            : sectorSelections) ?? [],
                    preferences:
                        (type === 'issue'
                            ? draftIssuePreferences
                            : type === 'committee'
                            ? draftCommitteePreferences
                            : type === 'sector'
                            ? draftSectorPreferences
                            : draftInsiderPreferences) ?? [],
                    // Filter out empty emails
                    emails: primaryEmail
                        ? [
                              primaryEmail,
                              ...(draftAdditionalEmails
                                  ?.filter((user) => validator.isEmail(user.email))
                                  .map((user) => user.email) ?? []),
                          ]
                        : [],
                    persionalIssues: persionalIssues ?? [],
                    lobbyIQIssues: lobbyIQIssues ?? [],
                    selectedLobbyIQIssues: selectedLobbyIQIssues ?? [],
                    maxLobbyIQIssues: 10,
                    handleEmailCheck: handleEmailPreferenceChange,
                    handleAddIssue,
                    handleRemoveIssue,
                })}
            </div>
        </Fade>
    );
};

export default EmailPreferencesPage;
